<cs-header>
  <cs-header-title>{{ mode === 'select' ? LL.foods.addFoods() : LL.foods.title() }}</cs-header-title>
  <cs-header-content>
    <!-- The search box and the chip line live in the header (#5901), so they stay at the top while
         the foods scroll under them and hide and show together with the header, like every other
         page's header controls. They are one band because the chips say what the results are
         filtered by, so they must never be separated from the box that produced them. -->
    <div class="search-band">
      <!-- Search bar. The wrapper is what the suggestion list below is positioned against, so the
           list floats over the page instead of pushing it down. -->
      <div class="search-area">
        <!-- The Scan Label (camera) and barcode buttons sit inside the right end of the box (#5521):
             both find a food by pointing the camera at it, so they belong with search, and Scan
             Label no longer needs its own row. They are laid over the shared cs-input from here
             rather than added to it, so no other use of cs-input changes. -->
        <div class="search-container" [class.search-container--two-icons]="labelScanEnabled">
          <cs-input #searchInput autocomplete="off" id="search-text" [(value)]="searchText"
            type="text" name="searchText" inputmode="search" [placeholder]="LL.foods.searchPlaceholder()"
            [iconName]="(showSpinner || suggestionRows.pending) ? 'spinner' : 'search'" (valueChange)="handleSearchChanged()"
            (keydown)="handleSearchKeydown($event)" (blur)="handleSearchBlur()"
            ariaRole="combobox" [ariaExpanded]="suggestionRows.rowsShown"
            [ariaControls]="suggestionRows.rowsShown ? 'food-suggestion-list' : undefined"
            [ariaActiveDescendant]="suggestionRows.rowsShown && suggestionRows.highlightedRow >= 0 ? suggestionRowId(suggestionRows.highlightedRow) : undefined"></cs-input>
          <div class="search-icons">
            @if (labelScanEnabled) {
              <cs-icon-button icon="camera" data-e2e="search-scan-label" [ariaLabel]="LL.labelScanner.title()" (click)="handleScanLabel()"></cs-icon-button>
            }
            <cs-icon-button icon="barcode" [ariaLabel]="LL.editMeal.addToMeal.scanBarcode()" (click)="handleScanBarcodeButtonClick($event)"></cs-icon-button>
          </div>
        </div>

        <!-- Whole search lines suggested while typing (#5410), each with how many foods hold it
             when the server answered (rows from the on-device word list have no count). Tapping a
             row, or Enter on the highlighted one, puts the line in the box and runs the search
             once. mousedown is cancelled so a tap does not take focus from the search box first:
             that blur would hide the rows before the tap landed. -->
        @if (suggestionRows.rowsShown) {
          <!-- the rows are left out of the Tab order (tabindex -1): the arrow keys move through them
               from the search box, which keeps focus, and tabbing onto a row would blur the box and
               hide the very rows being tabbed to -->
          <div id="food-suggestion-list" class="suggestion-list" role="listbox" [attr.aria-label]="LL.foods.suggestions.listLabel()"
            (mousedown)="$event.preventDefault()">
            <!-- a staple row (food staples plan 4b) is tracked by its food id: its name can equal a line -->
            @for (row of suggestionRows.list.rows; track row.stapleId ?? row.text; let i = $index) {
              <button type="button" class="suggestion-row" role="option" tabindex="-1" [id]="suggestionRowId(i)"
                [class.highlighted]="i === suggestionRows.highlightedRow"
                [class.suggestion-row--picture]="staplePictureForRow(row) !== undefined"
                [class.suggestion-row--in-picture-list]="staplePicturesShownInList()"
                [attr.aria-selected]="i === suggestionRows.highlightedRow" [attr.aria-label]="suggestionRowLabel(row)"
                (click)="pickSuggestion(row, i)">
                <!-- a staple row shows its own picture (staple pictures plan, #5694); the shield is the
                     fallback, so the row is still marked as a staple when there is no picture or it
                     could not be loaded. An ordinary row then gets a spacer as wide as a picture, so
                     every row's name starts in the same place. -->
                @if (row.stapleId) {
                  @if (staplePictureForRow(row); as picture) {
                    <!-- the size is the theme's `$staple-picture-size-row`, set in foods.scss; the
                         square aspect ratio here reserves the row's space while the file loads -->
                    <img class="suggestion-staple-picture" [src]="picture.small" alt=""
                      data-e2e="suggestion-staple" (error)="handleStaplePictureError(picture.small)">
                  } @else {
                    <cs-icon name="shield-checkmark" fill [ngClass]="'suggestion-staple-icon'" data-e2e="suggestion-staple"></cs-icon>
                  }
                } @else if (staplePicturesShownInList()) {
                  <span class="suggestion-staple-spacer" aria-hidden="true"></span>
                }
                <span class="suggestion-text">{{ row.text }}</span>
                @if (row.count !== undefined) {
                  <span class="suggestion-count">{{ row.count }}</span>
                }
              </button>
            }
          </div>
        } @else if (suggestionRows.loadingShown) {
          <!-- the list opens at once while the first rows are on their way, so a slow connection
               does not look like there is nothing to suggest -->
          <div class="suggestion-list suggestion-list--loading" role="status" data-e2e="suggestions-loading"
            [attr.aria-label]="LL.foods.suggestions.loading()">
            <cs-spinner></cs-spinner>
          </div>
        }
      </div>

      <!-- The chip line, which replaced the four type tabs (#5549), drawn as design option D (#5902)
           from the shared cs-button chip form and cs-icon-button. Left to right: one chip for the
           type, a divider, the filters that are ON (blue, each with an ×), then My Foods and
           Favorites while they are off. The chips scroll sideways and fade out at the right edge;
           the Filters icon after them stays put. -->
      <div class="chip-row">
        <div class="chip-strip">
          <!-- the type chip: "All" with an arrow while nothing is chosen, otherwise the type in blue
               with an × back to All. Tapping the name opens the picker either way; the × is its
               own tap target. -->
          <cs-button chip [attr.outlined]="filters.type === FoodTypeFilter.All ? '' : null"
            [toggle]="filters.type !== FoodTypeFilter.All" data-e2e="type-chip"
            [label]="typeFilterLabel(filters.type)"
            [icon-right]="filters.type === FoodTypeFilter.All ? 'arrow-point-down' : 'close'"
            [ariaLabel]="typeFilterLabel(filters.type)"
            [iconRightLabel]="filters.type === FoodTypeFilter.All ? undefined : LL.foods.filters.removeFilter({ filter: typeFilterLabel(filters.type) })"
            (click)="handleTypeChipClick($event)" (iconRightClick)="setTypeFilter(FoodTypeFilter.All)"></cs-button>

          <div class="chip-divider" aria-hidden="true"></div>

          <!-- a tap anywhere on a filter chip flips it, so the × on one that is on is only a sign -->
          @for (chip of filterChips; track chip.id) {
            <cs-button chip [toggle]="chip.on" [attr.data-e2e]="'filter-chip-' + chip.id"
              [label]="filterLabel(chip.id)" [icon-right]="chip.on ? 'close' : undefined"
              [ariaLabel]="chip.on ? removeFilterLabel(chip.id) : undefined"
              (click)="handleChipClick(chip)"></cs-button>
          }
        </div>

        <!-- Filters, at the end of the row (#5521): an icon that turns blue with a count while filters
             are on, the same shared button and count badge as the workout set bell (#5902) -->
        <cs-icon-button icon="tune" [badgeMinimum]="1" [badgePosition]="BadgePosition.TopRight" [badge]="activeFilterCount"
          [attr.link-color]="activeFilterCount > 0 ? '' : null" data-e2e="filters-button"
          [ariaLabel]="LL.foods.buttons.filters()" (click)="handleFiltersButtonClick()"></cs-icon-button>
      </div>
    </div>
  </cs-header-content>
</cs-header>
<cs-content>
  <div class="centered-holder">
    <!-- Food List -->
    <div class="food-list">
      <!-- Loading spinner -->
      @if (showSpinner) {
        <div class="food-search-spinner">
          <cs-spinner></cs-spinner>
        </div>
      }

      <!-- What is being said about the SPELLING of the search on screen (#5410). Above the
           results for the same reason the cards below are: somebody who reads a screen of
           unrelated foods without learning the search was misspelled has been failed twice. A
           correction we are confident about has already been run, and a second line offers the
           way back; a less certain one is only offered, and the results are still what was
           typed. -->
      <!-- A quiet line rather than a card: this is an aside about spelling, and the results
           themselves are what the person came for. The shape is the one every search engine uses
           -- a short label, then the search line itself beside it. Which line is pressable, and
           which is italic, differs by line and is decided in the stylesheet; only the suggestion
           is coloured like a warning. data-e2e names each line, because two info cards can be on
           screen in this same region and a test matching any of them would press the wrong
           thing.

           Each pressable search line is a real <button>: it runs a search rather than going
           anywhere, so it must stay a button for the keyboard and for a screen reader however it
           looks. Its visible text is only the search line, which would read as a bare word out of
           context, so aria-label gives the whole instruction instead. -->
      @if (correction.kind === SearchCorrectionKind.Offer) {
        <p class="search-correction search-correction--suggested" data-e2e="search-correction-offer">
          <span>{{ LL.foods.messages.didYouMeanLabel() }}</span>
          <button type="button" class="search-correction-query"
            [attr.aria-label]="LL.foods.buttons.searchCorrection({ phrase: correction.phrase })"
            (click)="takeOfferedCorrection()">{{ correction.phrase }}</button>
        </p>
      } @else if (correction.kind === SearchCorrectionKind.Apply) {
        <p class="search-correction" data-e2e="search-correction-applied">
          <span>{{ LL.foods.messages.showingResultsForLabel() }}</span>
          <!-- not pressable: this one says what WAS searched, and it is already on screen, so it
               must not be coloured as though there were somewhere to go -->
          <span class="search-correction-query search-correction-query--shown">{{ correction.phrase }}</span>
        </p>
        <p class="search-correction" data-e2e="search-correction-revert">
          <span>{{ LL.foods.messages.searchInsteadForLabel() }}</span>
          <button type="button" class="search-correction-query search-correction-query--plain"
            [attr.aria-label]="LL.foods.buttons.searchTypedInstead({ phrase: correctionTypedText })"
            (click)="refuseCorrection()">{{ correctionTypedText }}</button>
        </p>
      }

      <!-- The card that explains a list holding nothing but foods already on this device
           (#5114). It sits ABOVE the results on purpose: the user who reported that ticket
           read a whole screen of unrelated foods without ever learning that the search had
           failed, and a note under the list would have been below the fold. Which card shows
           is decided in foods-list-message.ts; each branch below states only how it LOOKS. -->
      @switch (searchCard) {
        <!-- The device looks offline. Being offline is ordinary, so this is an `info` card. -->
        @case (FoodsSearchCard.OfflineShowingLocalFoods) {
          <cs-info-card type="info" [dismissible]="false"
            [message]="LL.foods.messages.offlineShowingLocalFoods()">
          </cs-info-card>
        }

        <!-- The same case for a barcode search, which is never offered the staples, so its
             wording must not mention them. -->
        @case (FoodsSearchCard.OfflineShowingLocalFoodsBarcode) {
          <cs-info-card type="info" [dismissible]="false"
            [message]="LL.foods.messages.offlineShowingLocalFoodsBarcode()">
          </cs-info-card>
        }

        <!-- The device believes it is online, so the food database itself is the problem.
             `warning` rather than `info`, and a Retry, because this one may well clear. -->
        @case (FoodsSearchCard.ServerProblemShowingLocalFoods) {
          <cs-info-card type="warning" [dismissible]="false"
            [message]="LL.foods.messages.searchUnreachableShowingLocalFoods()"
            [actionText]="LL.foods.buttons.retrySearch()" (actionClick)="retrySearch()">
          </cs-info-card>
        }

        <!-- The same case for a barcode search, which is never offered the staples. -->
        @case (FoodsSearchCard.ServerProblemShowingLocalFoodsBarcode) {
          <cs-info-card type="warning" [dismissible]="false"
            [message]="LL.foods.messages.searchUnreachableShowingLocalFoodsBarcode()"
            [actionText]="LL.foods.buttons.retrySearch()" (actionClick)="retrySearch()">
          </cs-info-card>
        }
      }

      <!-- Food cards.
           No "+" button on these (#4967, owner decision 2026-08-29): it sat on the card and
           added the food immediately with a default amount, while tapping the card itself
           opened the portion screen to choose the amount. Two buttons on one card doing
           different things read as confusing, so tapping the card is now the only way to add
           a food from here. -->
      <!-- the default list is all foods logged before, so one heading says it once and the
           cards drop their per-card clock; search results keep the clock, where it tells foods apart -->
      @if (showLoggedBeforeHeading) {
        <h3 class="logged-before-heading" data-e2e="logged-before-heading">{{ LL.foods.badges.usedBefore() }}</h3>
      }
      @for (food of foods; track food._id) {
        <cs-food-card
          [food]="food"
          [nutrients]="getFoodDisplayNutrients(food)"
          [servingUnit]="getFoodServingUnit(food)"
          [servingAmount]="getFoodServingAmount(food)"
          [weight]="getFoodWeight(food)"
          [weightUnit]="'g'"
          [measuresLoading]="food.measuresLoading"
          [description]="getFoodDescription(food)"
          [imageUrl]="hasImage(food) ? imageService.getImageThumbnailUrl(food.image) : null"
          [staplePictureUrl]="staplePictureForFood(food)?.small"
          [isLogMeal]="isLogMealEntry(food)"
          [isUsedBefore]="!showLoggedBeforeHeading && isFoodUsedBefore(food)"
          [liked]="food.liked ?? null"
          [inconsistentNutrientsInfo]="food.inconsistentNutrientsInfo"
          [noNutritionData]="!!food.noNutritionData"
          [showMenuButton]="true"
          (click)="handleFoodClicked(food)"
          (menuClick)="handleFoodMenu($event)"
          (imageError)="handleImageLoadError($event)"
          tappable>
        </cs-food-card>
      }

      <!-- The one message that closes the list. Which of these shows is decided in
           foods-list-message.ts, so exactly one can ever be picked and a unit test can say so.
           Each branch below states only how its own message LOOKS. -->
      @switch (listMessage) {
        <!-- Nothing has been searched for yet. -->
        @case (FoodsListMessage.UseSearchBar) {
          <cs-empty-state
            iconName="search" [title]="LL.foods.messages.useSearchBar()">
          </cs-empty-state>
        }

        <!-- Nothing has been searched for, a filter is on, and nothing is listed: the filter
             may be why, so offer to clear it. -->
        @case (FoodsListMessage.NothingMatchesFilters) {
          <cs-empty-state
            iconName="search" [title]="LL.foods.messages.nothingMatchesFilters()"
            [subtext]="LL.foods.messages.clearFiltersOrSearch()">
            <cs-button secondary small data-e2e="clear-filters-button" (click)="resetFilters()">
              {{ LL.foods.buttons.clearFilters() }}
            </cs-button>
          </cs-empty-state>
        }

        <!-- The search itself failed (#4100). Opening this page lists the foods you logged
             before; when that read cannot be done at all, say so and offer a retry instead of
             leaving a spinner running forever. Shown for a typed search too, where it replaces
             the misleading "couldn't find what you're looking for". -->
        @case (FoodsListMessage.SearchFailed) {
          <cs-empty-state
            iconName="cloud-offline" [title]="LL.foods.messages.searchFailedTitle()"
            [subtext]="LL.foods.messages.searchFailedHint()">
            <cs-button secondary small icon="refresh" (click)="retrySearch()">
              {{ LL.foods.buttons.retrySearch() }}
            </cs-button>
          </cs-empty-state>
        }

        <!-- No results found (barcode) -->
        @case (FoodsListMessage.NoResultsForBarcode) {
          <cs-empty-state
            [iconName]="labelScanEnabled ? 'camera' : 'barcode'"
            [title]="labelScanEnabled ? LL.foods.messages.noFoodByBarcodeScanLabel() : LL.foods.messages.noFoodByBarcode()"
            [subtext]="labelScanEnabled ? LL.foods.messages.noFoodByBarcodeScanLabelHint() : null">
            @if (labelScanEnabled) {
              <cs-button secondary small icon="camera" (click)="handleScanLabel()">
                {{ LL.labelScanner.title() }}
              </cs-button>
            } @else {
              <cs-button secondary small [icon]="foodTypeIcons[FoodType.Food].name" (click)="handleCreateFood()">
                {{ LL.foods.buttons.createFood() }}
              </cs-button>
            }
          </cs-empty-state>
        }

        <!-- No results found (text search) -->
        @case (FoodsListMessage.NoResultsForText) {
          <cs-empty-state
            iconName="search" [title]="LL.foods.messages.notFoundWhat()"
            [subtext]="LL.foods.messages.useButtonsBelow()">
            <cs-button secondary small [icon]="foodTypeIcons[FoodType.Recipe].name" (click)="handleCreateRecipe()">
              {{ LL.foods.buttons.createRecipe() }}
            </cs-button>
            <cs-button secondary small [icon]="foodTypeIcons[FoodType.Food].name" (click)="handleCreateFood()">
              {{ LL.foods.buttons.createFood() }}
            </cs-button>
          </cs-empty-state>
        }

        <!-- No results for a text search with a filter on: the filter may be why -->
        @case (FoodsListMessage.NoResultsForTextWithFilters) {
          <cs-empty-state
            iconName="search" [title]="LL.foods.messages.notFoundWhat()"
            [subtext]="LL.foods.messages.clearFiltersOrCreate()">
            <cs-button secondary small data-e2e="clear-filters-button" (click)="resetFilters()">
              {{ LL.foods.buttons.clearFilters() }}
            </cs-button>
            <cs-button secondary small [icon]="foodTypeIcons[FoodType.Recipe].name" (click)="handleCreateRecipe()">
              {{ LL.foods.buttons.createRecipe() }}
            </cs-button>
            <cs-button secondary small [icon]="foodTypeIcons[FoodType.Food].name" (click)="handleCreateFood()">
              {{ LL.foods.buttons.createFood() }}
            </cs-button>
          </cs-empty-state>
        }

        <!-- The search couldn't reach the server (connection problem, #3743). Distinct from
             "not found" so a network blip is never read as "this food does not exist". -->
        @case (FoodsListMessage.NoResultsConnectionError) {
          <cs-empty-state
            iconName="cloud-offline" [title]="LL.foods.messages.searchConnectionTitle()"
            [subtext]="LL.foods.messages.searchConnectionHint()">
            <cs-button secondary small icon="refresh" (click)="retrySearch()">
              {{ LL.foods.buttons.retrySearch() }}
            </cs-button>
          </cs-empty-state>
        }

        <!-- Barcode results showed, but the label scanner may do better -->
        @case (FoodsListMessage.ResultsOfferScanLabel) {
          <cs-empty-state class="end-marker-scan"
            [title]="LL.foods.messages.wrongFoodScanLabel()"
            [subtext]="LL.foods.messages.wrongFoodScanLabelHint()">
            <cs-button secondary small icon="camera" (click)="handleScanLabel()">
              {{ LL.labelScanner.title() }}
            </cs-button>
          </cs-empty-state>
        }

        <!-- Results showed, but they might not be what the user wanted -->
        @case (FoodsListMessage.ResultsNotWhatYouWanted) {
          <div class="end-marker">
            <p>{{ LL.foods.messages.notFoundWhat() }}</p>
            <p>{{ LL.foods.messages.useButtonsBelow() }}</p>
          </div>
        }
      }
    </div>

    <!-- The floating "+ New" button (#5521): Create Food, Create Recipe and Add Macros, which used
         to be a band of buttons above the list. Last in the column and sticky, so it stays in view
         while the list scrolls and settles below the last card at the end. -->
    <div class="new-button-holder">
      <cs-button icon="add" data-e2e="foods-new-button" (click)="handleNewButtonClick($event)">
        {{ LL.foods.buttons.new() }}
      </cs-button>
    </div>
  </div>
</cs-content>
